import React, { useState } from "react";
import PropTypes from "prop-types";
import { Alert, Button, Drawer, Radio, Select } from "@opendate-app/setlist";
import { useStaffingPermissionsContext } from "./StaffingPermissionsContext";

const DEFAULT_EFFECT = "default";

const PermissionGrantDrawer = ({ draft, onClose }) => {
  const { permissions, roles, members, departments, positions, paths, mutate } = useStaffingPermissionsContext();
  const existing = draft.grant;
  const forRole = draft.grantee_type === "Role";
  const [memberId, setMemberId] = useState(draft.grantee_id || "");
  const [permissionKey, setPermissionKey] = useState(draft.permission || "");
  const [effect, setEffect] = useState(existing?.effect || (forRole ? DEFAULT_EFFECT : "allow"));
  const [departmentIds, setDepartmentIds] = useState(existing?.staff_department_ids || []);
  const [positionIds, setPositionIds] = useState(existing?.staff_position_ids || []);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState([]);

  const permission = permissions.find((option) => option.key === permissionKey);
  const role = forRole ? roles.find((option) => option.id === draft.grantee_id) : null;
  const scopeable = permission && !permission.teamWide && effect !== DEFAULT_EFFECT;
  const canSubmit = !saving && permissionKey && (forRole || memberId);

  const save = async () => {
    if (!canSubmit) return;
    setSaving(true);
    const body = {
      staffing_permission_grant: {
        grantee_type: draft.grantee_type,
        grantee_id: forRole ? draft.grantee_id : memberId,
        permission: permissionKey,
        effect,
        staff_department_ids: scopeable ? departmentIds : [],
        staff_position_ids: scopeable ? positionIds : [],
      },
    };
    let result = { ok: true, errors: [] };
    if (effect === DEFAULT_EFFECT && existing) {
      result = await mutate("delete", `${paths.grants}/${existing.id}`, undefined, "Could not reset this permission.");
    } else if (existing) {
      result = await mutate("patch", `${paths.grants}/${existing.id}`, body, "Could not save this permission.");
    } else if (effect !== DEFAULT_EFFECT) {
      result = await mutate("post", paths.grants, body, "Could not save this permission.");
    }
    setSaving(false);
    if (result.ok) {
      onClose();
    } else {
      setErrors(result.errors);
    }
  };

  const remove = async () => {
    setSaving(true);
    const result = await mutate("delete", `${paths.grants}/${existing.id}`, undefined, "Could not remove this override.");
    setSaving(false);
    if (result.ok) {
      onClose();
    } else {
      setErrors(result.errors);
    }
  };

  const effectOptions = [
    forRole && { value: DEFAULT_EFFECT, label: `Use the default (${role?.defaults.includes(permissionKey) ? "allowed" : "not allowed"})` },
    { value: "allow", label: "Allow" },
    { value: "deny", label: "Deny" },
  ].filter(Boolean);

  return (
    <Drawer
      open
      backdrop
      width={520}
      title={forRole ? `${role?.name}: ${permission?.label}` : existing ? "Edit override" : "Add a person override"}
      subtitle={permission?.description}
      onClose={onClose}
      footer={
        <div className="d-flex w-100" style={{ gap: 8 }}>
          {!forRole && existing && (
            <Button variant="secondary" disabled={saving} onClick={remove}>
              Remove override
            </Button>
          )}
          <div className="ml-auto d-flex" style={{ gap: 8 }}>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" disabled={!canSubmit} onClick={save}>
              {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      }
    >
      {errors.length > 0 && (
        <div className="mb-3">
          <Alert color="error" title="Could not save" description={errors.join(" ")} />
        </div>
      )}

      {!forRole && (
        <>
          <div className="mb-3">
            <Select
              id="staffing-permission-member"
              label="Person"
              searchable
              disabled={Boolean(existing)}
              options={members
                .filter((member) => member.editable || member.value === memberId)
                .map((member) => ({ value: member.value, label: `${member.label} (${member.owner ? "Owner" : member.role})` }))}
              value={memberId}
              onChange={(value) => setMemberId(value || "")}
            />
          </div>
          <div className="mb-3">
            <Select
              id="staffing-permission-key"
              label="Permission"
              disabled={Boolean(existing)}
              options={permissions.filter((option) => option.editable || option.key === permissionKey).map((option) => ({ value: option.key, label: option.label }))}
              value={permissionKey}
              onChange={(value) => setPermissionKey(value || "")}
            />
          </div>
        </>
      )}

      <div className="mb-3">
        <strong className="d-block mb-2">Access</strong>
        {effectOptions.map((option) => (
          <div key={option.value} className="mb-1">
            <Radio
              id={`staffing-permission-effect-${option.value}`}
              name="staffing-permission-effect"
              label={option.label}
              checked={effect === option.value}
              onChange={() => setEffect(option.value)}
            />
          </div>
        ))}
      </div>

      {scopeable && (
        <>
          <p className="text-muted">Leave both empty to cover every department. A department includes all of its positions, even ones added later.</p>
          <div className="mb-3">
            <Select
              id="staffing-permission-departments"
              label="Departments"
              multiple
              clearable
              options={departments.filter((option) => !option.archived || departmentIds.includes(option.value))}
              value={departmentIds}
              onChange={(value) => setDepartmentIds(value || [])}
            />
          </div>
          <Select
            id="staffing-permission-positions"
            label="Positions"
            multiple
            clearable
            searchable
            options={positions.filter((option) => !option.archived || positionIds.includes(option.value))}
            value={positionIds}
            onChange={(value) => setPositionIds(value || [])}
          />
        </>
      )}
    </Drawer>
  );
};

PermissionGrantDrawer.propTypes = {
  draft: PropTypes.shape({
    grantee_type: PropTypes.oneOf(["Role", "TeamMembership"]).isRequired,
    grantee_id: PropTypes.number,
    permission: PropTypes.string,
    grant: PropTypes.object,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
};

export default PermissionGrantDrawer;
